/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, AlertIcon, Button, Heading, Stack, Text } from '@chakra-ui/react';
import { CognitoRefreshToken, CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useState } from 'react';
import { AuthStage, cookieStorage } from '../..';
import { Method, queryData } from '../../../../../utilities/fetch';
import { Title } from '../../../../atoms/Title';
import { refreshSessionAsync } from '../../refreshSession';

export interface IProps {
  code: string;
  apiUrl: string;
  appClient: string;
  setAuthStage: (stage: AuthStage) => void;
  setUser: (username: CognitoUser) => void;
  setSession: (session: CognitoUserSession) => void;
  setUsername: (username: string) => void;
  setIsLoading: (show: boolean) => void;
}

const SingleSignOnCallback = (props: IProps) => {
  const { code, apiUrl, appClient, setAuthStage, setUser, setSession, setUsername, setIsLoading } = props;
  const [singleSignOnTokenError, setSingleSignOnTokenError] = useState('');

  useEffect(() => {
    const handleCallBack = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const errorDescription = urlParams.get('error_description');
        const decodedErrorDescription = decodeURIComponent(errorDescription || '');
        if (decodedErrorDescription) {
          setIsLoading(false);
          setSingleSignOnTokenError(decodedErrorDescription);
          return;
        }

        if (singleSignOnTokenError !== '') {
          setIsLoading(false);
          return;
        }

        const ssoCallbackResult = await handleSSOCallback(code);

        if (!ssoCallbackResult) {
          setIsLoading(false);
          return;
        }

        // Slight hack due to the rubbish return type from user-management
        // If the call errors it returns a string body with the error message
        if (typeof ssoCallbackResult === 'string' || ssoCallbackResult instanceof String) {
          setIsLoading(false);
          setSingleSignOnTokenError(String(ssoCallbackResult));
          return;
        }

        const { refreshToken, userpool, clientId, username } = ssoCallbackResult;

        const userPool = new CognitoUserPool({
          UserPoolId: userpool,
          ClientId: clientId,
          Storage: cookieStorage
        });
        const newUser = new CognitoUser({
          Username: username,
          Pool: userPool!,
          Storage: cookieStorage
        });

        const token = new CognitoRefreshToken({ RefreshToken: refreshToken });

        const session = await refreshSessionAsync(newUser, token);

        const jwtToken = session.getIdToken().getJwtToken();
        const decodedToken: any = jwtDecode(jwtToken);

        cookieStorage.setItem('username', username);
        cookieStorage.setItem('appClient', clientId); // for NextJS app
        cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
        cookieStorage.setItem('uni', decodedToken['custom:University']); // for NextJS app

        setUser(newUser);
        setSession(session);
        setUsername(username);
        setAuthStage(AuthStage.NONE);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        if (err.message) {
          const errorMessage = `${err.message} - Please contact your administrator.`;
          setSingleSignOnTokenError(errorMessage);
        } else if (typeof err === 'string') {
          setSingleSignOnTokenError(err);
        } else {
          setSingleSignOnTokenError('An unknown error occured.');
        }
      }
    };
    handleCallBack();
  }, []);

  const handleSSOCallback = async (code: string) => {
    if (typeof code === 'string' && code !== '') {
      // TODO: Give this correct request and response types
      const resp = await queryData<any, any>({
        uri: `/auth/sso/token`,
        domain: apiUrl,
        method: Method.Post,
        body: { clientId: appClient, code, origin: window.location.origin }
      });
      return resp;
    }
    return '';
  };

  const handleRetryLogin = () => {
    setSingleSignOnTokenError('');
    window.location.href = window.location.origin;
  };

  const title = 'Log in to CAS Shield';

  return (
    <>
      <Title title={title} />

      <Heading as="h1" mb="6">
        {title}
      </Heading>

      {singleSignOnTokenError !== '' ? (
        <Alert status="error" mb="4">
          <AlertIcon />
          {[singleSignOnTokenError]}
        </Alert>
      ) : null}
      {/* {props.server_errors && props.server_errors.length > 0 ? (
            <Alert status="error" mb="4">
              <AlertIcon />
              {props.server_errors}
            </Alert>
          ) : null} */}

      {singleSignOnTokenError !== '' ? (
        <Stack>
          <Button onClick={handleRetryLogin} size="lg" variant="solid">
            Try login again
          </Button>
        </Stack>
      ) : (
        <Text>Logging in...</Text>
      )}
    </>
  );
};

export default SingleSignOnCallback;
